import type { AriaRole, KeyboardEvent, MouseEvent } from 'react'

// A click on one of these inside a row does its own job and doesn't open the row.
const INTERACTIVE = 'a, button, input, select, textarea, label, summary, [role="button"], [role="link"]'

export interface RowOpenOptions {
  /**
   * The role for the element. Leave it out for a table row, which already has
   * one; pass 'button' for a card that does nothing but open something.
   */
  role?: AriaRole
}

export interface RowOpenProps<T extends HTMLElement> {
  tabIndex?: number
  role?: AriaRole
  onClick?: (event: MouseEvent<T>) => void
  onKeyDown?: (event: KeyboardEvent<T>) => void
}

/**
 * Props that make a row or card that opens something work without a mouse:
 * it takes focus and opens on Enter or Space, as a button would, and a click
 * on a button, link or input inside it is left to that control. Without
 * `onOpen` it returns nothing, so the element stays out of the tab order.
 */
export function rowOpenProps<T extends HTMLElement = HTMLElement>(
  onOpen?: () => void,
  { role }: RowOpenOptions = {},
): RowOpenProps<T> {
  if (!onOpen) return {}
  return {
    tabIndex: 0,
    ...(role ? { role } : {}),
    onClick: (event) => {
      const inner = (event.target as Element).closest(INTERACTIVE)
      if (inner && inner !== event.currentTarget && event.currentTarget.contains(inner)) return
      onOpen()
    },
    onKeyDown: (event) => {
      if (event.target !== event.currentTarget) return
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        onOpen()
      }
    },
  }
}
